import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { AccountTree, Psychology } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import {
  ChatbotConfiguration,
  ChatbotType,
  FlowDocument,
} from "../../models/flow.model";
import { flowApi, flowApiErrorMessage } from "../../services/flow.service";

interface ChatbotModeSettingsProps {
  businessId: string;
}

const flowKey = (flow: FlowDocument) => `${flow.flowId}:${flow.version}`;

export default function ChatbotModeSettings({
  businessId,
}: ChatbotModeSettingsProps) {
  const [configuration, setConfiguration] =
    useState<ChatbotConfiguration | null>(null);
  const [flows, setFlows] = useState<FlowDocument[]>([]);
  const [mode, setMode] = useState<ChatbotType>("AGENTIC_AI");
  const [selectedFlowKey, setSelectedFlowKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const publishedFlows = useMemo(
    () => flows.filter((flow) => flow.status === "PUBLISHED"),
    [flows],
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!businessId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const [nextConfiguration, nextFlows] = await Promise.all([
          flowApi.configuration(businessId),
          flowApi.list(businessId),
        ]);
        if (!active) return;

        const published = nextFlows.filter(
          (flow) => flow.status === "PUBLISHED",
        );
        const activeFlow = published.find(
          (flow) =>
            flow.flowId === nextConfiguration.activeFlowId &&
            flow.version === nextConfiguration.activeFlowVersion,
        );

        setConfiguration(nextConfiguration);
        setFlows(nextFlows);
        setMode(nextConfiguration.type);
        setSelectedFlowKey(
          activeFlow ? flowKey(activeFlow) : published[0] ? flowKey(published[0]) : "",
        );
      } catch (reason) {
        if (active) setError(flowApiErrorMessage(reason));
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [businessId]);

  const save = async () => {
    if (!configuration) return;

    const selectedFlow = publishedFlows.find(
      (flow) => flowKey(flow) === selectedFlowKey,
    );
    if (mode === "FLOW" && !selectedFlow) {
      setError("Publish and select a flow before enabling Flow-Based mode.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const nextConfiguration: ChatbotConfiguration = {
        ...configuration,
        type: mode,
        ...(selectedFlow
          ? {
              activeFlowId: selectedFlow.flowId,
              activeFlowVersion: selectedFlow.version,
            }
          : {}),
      };
      const saved = await flowApi.updateConfiguration(
        businessId,
        nextConfiguration,
      );
      setConfiguration(saved);
      setMode(saved.type);
      setSuccess(
        saved.type === "FLOW"
          ? "Flow-Based chatbot activated."
          : "Agentic AI chatbot activated.",
      );
    } catch (reason) {
      setError(flowApiErrorMessage(reason));
    } finally {
      setSaving(false);
    }
  };

  if (!businessId) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        Configure a WhatsApp business ID before choosing a chatbot mode.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ p: 6 }}>
        <CircularProgress />
        <Typography color="text.secondary">
          Loading chatbot configuration…
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Box>
          <Typography variant="h6" fontWeight={700}>
            Choose how WhatsApp conversations are handled
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            This setting controls new inbound conversations for this business.
            Existing Flow sessions remain pinned to the version on which they
            started.
          </Typography>
        </Box>

        {configuration && (
          <Alert severity="info">
            Currently active: {configuration.type === "FLOW" ? "Flow-Based chatbot" : "Agentic AI"}
          </Alert>
        )}

        <FormControl>
          <FormLabel id="chatbot-mode-label">Chatbot mode</FormLabel>
          <RadioGroup
            aria-labelledby="chatbot-mode-label"
            value={mode}
            onChange={(event) => setMode(event.target.value as ChatbotType)}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={1}>
              <Card
                variant="outlined"
                sx={{ flex: 1, borderColor: mode === "AGENTIC_AI" ? "primary.main" : "divider" }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Psychology color="primary" />
                    <Box>
                      <FormControlLabel
                        value="AGENTIC_AI"
                        control={<Radio />}
                        label={<Typography fontWeight={700}>Agentic AI</Typography>}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        Uses the existing AI agent, tools, instructions, and fallback behavior.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{ flex: 1, borderColor: mode === "FLOW" ? "primary.main" : "divider" }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <AccountTree color="primary" />
                    <Box>
                      <FormControlLabel
                        value="FLOW"
                        control={<Radio />}
                        label={<Typography fontWeight={700}>Flow-Based</Typography>}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        Runs a published deterministic conversation flow.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </RadioGroup>
        </FormControl>

        {mode === "FLOW" && (
          <Stack spacing={1.5}>
            {publishedFlows.length ? (
              <FormControl fullWidth>
                <InputLabel id="active-flow-label">Published flow</InputLabel>
                <Select
                  labelId="active-flow-label"
                  value={selectedFlowKey}
                  label="Published flow"
                  onChange={(event) => setSelectedFlowKey(event.target.value)}
                >
                  {publishedFlows.map((flow) => (
                    <MenuItem key={flowKey(flow)} value={flowKey(flow)}>
                      {flow.name} · version {flow.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="warning">
                No published flow is available. Create, validate, and publish a
                flow before enabling this mode.
              </Alert>
            )}
            <Button
              component={RouterLink}
              to="/chatbot-builder"
              startIcon={<AccountTree />}
              sx={{ alignSelf: "flex-start" }}
            >
              Open Chatbot Builder
            </Button>
          </Stack>
        )}

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={
              saving ||
              !configuration ||
              (mode === "FLOW" && !selectedFlowKey)
            }
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : "Save chatbot mode"}
          </Button>
          {configuration?.type === mode && (
            <Chip size="small" color="success" label="Current mode" />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
